// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { rgba } from 'polished';
import styledTheme from 'styled-theming';
import styled, { css } from 'styled-components';

import fonts from '@legacy/styles/fonts';
import Icon from '@legacy/moon/particles/Icon';
import colors from '@legacy/moon/particles/colors';
import Container from '@legacy/moon/Layout/Container';
import breakpoints from '@legacy/shared/styles/Breakpoints';

const ImgStyled = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 12px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DropdownIcon = styled(({ isDisabled, menuIsOpen, isCompact, theme, customDropdownIcon, ...rest }) => (
  <Icon {...rest} />
))`
  color: ${({
    isDisabled,
    theme: {
      colors: { indicator },
    },
  }) => (isDisabled ? indicator.disabled : indicator.default)};
  display: block;
  position: absolute;
  top: ${({ menuIsOpen }) => (menuIsOpen ? '19px' : '22px')};
  right: 20px;
  font-size: 8px;
  transition: transform 0.2s;
  transform: rotateZ(${({ menuIsOpen }) => (menuIsOpen ? '180' : '0')}deg);
  &:before {
    font-weight: bold;
  }
  @media only screen and (min-width: ${breakpoints.screenDesktop}) {
    top: ${({ menuIsOpen, theme: { isCompact } }) => {
      if (isCompact) return '7px';
      return menuIsOpen ? '15px' : '16px';
    }};
  }
  ${({ theme: { customDropdownIcon: themedDropdownIcon } }) => themedDropdownIcon};
`;

const RemoveIcon = styled(Icon)`
  cursor: pointer;
  font-size: 20px;
`;

const placeholderIconTheme = styledTheme('product', {
  light: css<Record<string, boolean>>`
    color: ${colors.steel};
    ${({ isDisabled }) =>
      isDisabled &&
      css`
        color: ${colors.titanium};
      `};
    ${({ error }) =>
      error &&
      css`
        color: ${colors.mars};
      `};
  `,
  dark: css<Record<string, boolean>>`
    color: ${colors.silver};
    ${({ isDisabled }) =>
      isDisabled &&
      css`
        color: ${colors.selenium};
      `};
    ${({ error }) =>
      error &&
      css`
        color: ${colors.mars};
      `};
  `,
  alpha: css<Record<string, boolean>>`
    color: ${colors.silver};
    ${({ isDisabled }) =>
      isDisabled &&
      css`
        color: ${colors.selenium};
      `};
    ${({ error }) =>
      error &&
      css`
        color: ${colors.mars};
      `};
  `,
});

const PlaceholderIcon = styled(Icon)`
  position: absolute;
  top: 12px;
  opacity: ${({ isOpen }) => (isOpen ? '0' : '1')};
  font-size: 22px;
  left: 12px;
  z-index: -1;
  transition: all 0.2s ease-in-out;

  @media screen and (min-width: ${breakpoints.screenDesktop}) {
    top: ${({ isOpen }) => (isOpen ? '-8px' : '7px')};
  }

  ${placeholderIconTheme};
`;

const Label = styled.label`
  font-family: inherit;
  color: ${({
    isDisabled,
    hasError,
    theme: {
      colors: { placeholder },
    },
    // eslint-disable-next-line no-nested-ternary
  }) => (hasError ? placeholder.error : isDisabled ? placeholder.disabled : placeholder.default)};
  font-size: ${({ isOpen }) => (isOpen ? '12px' : '16px')};
  font-weight: 300;
  line-height: ${({ isOpen }) => (isOpen ? '17px' : '22px')};
  margin: ${({ theme: { spacing } }) => `0 ${spacing.baseUnit * 2}px`};
  position: absolute;
  top: ${({ isOpen }) => (isOpen ? '-8px' : '13px')};
  transition: top 0.2s, font-size 0.2s;
  z-index: 1;
  background-color: ${({
    isOpen,
    theme: {
      colors: { background },
    },
  }) => (isOpen ? background.default : 'transparent')};
  padding: ${({ theme: { spacing } }) => `0 ${spacing.menuGutter}px`};
  border-radius: 2px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: calc(100% - 50px);
  left: ${({ hasIcon, isOpen }) => (hasIcon && !isOpen ? '26px' : '0')};

  @media screen and (min-width: ${breakpoints.screenDesktop}) {
    top: ${({ isOpen }) => (isOpen ? '-15px' : '8px')};
  }
`;

const DescriptionTheme = styledTheme('product', {
  default: css`
    font-family: ${fonts.bitso};
  `,
});

const Description = styled.label`
  display: block;
  color: ${colors.selenium};
  font-style: normal;
  font-weight: 300;
  font-size: 13px;
  line-height: 19px;
  letter-spacing: 0.1px;
  z-index: -1;
  margin: 4px 0 0 12px;

  ${({ error }) =>
    error &&
    css`
      color: ${colors.mars};
      &:before {
        color: ${colors.mars};
      }
    `};
  ${({ disabled }) =>
    disabled &&
    css`
      color: ${colors.silver};
      &:before {
        color: ${colors.silver};
      }
    `};

  ${DescriptionTheme};
`;

const descriptionTransition = css`
  .input-description-transition-enter {
    transform: translateY(-35px);
    height: 0px;
    margin-top: 0px;
  }
  .input-description-transition-enter-active {
    transform: translateY(0);
    height: 20px;
    margin-top: 4px;
    transition: all 0.2s ease-in-out;
  }
  .input-description-transition-exit {
    transform: translateY(0);
    height: 20px;
    margin-top: 4px;
  }
  .input-description-transition-exit-active {
    transform: translateY(-35px);
    height: 0px;
    margin-top: 0px;
    transition: all 0.2s ease-in-out;
  }
`;

// Input styles
const containerTheme = styledTheme('product', {
  light: css<Record<string, boolean>>`
    &:before {
      background: ${colors.light};
      ${({ autofilled }) =>
        autofilled &&
        css`
          background: linear-gradient(180deg, ${colors.light} 9.38%, ${colors.aluminium} 100%);
        `};
      ${({ error }) =>
        error &&
        css`
          background: linear-gradient(180deg, ${colors.light} 0%, ${colors.halo} 100%);
        `};
    }
  `,
  dark: css<Record<string, boolean>>`
    &:before {
      background: ${colors.blackhole};
      ${({ autofilled }) =>
        autofilled &&
        css`
          background: linear-gradient(180deg, ${colors.blackhole} 0%, #2b3339 100%);
        `};
      ${({ error }) =>
        error &&
        css`
          background: linear-gradient(180deg, ${colors.blackhole} 0%, ${colors.carbonite} 69.79%);
        `};
    }
  `,
  alpha: css<Record<string, boolean>>`
    &:before {
      background: ${colors.blackhole};
      ${({ autofilled }) =>
        autofilled &&
        css`
          background: linear-gradient(180deg, ${colors.blackhole} 0%, #2b3339 100%);
        `};
      ${({ error }) =>
        error &&
        css`
          background: linear-gradient(180deg, ${colors.blackhole} 0%, ${colors.carbonite} 69.79%);
        `};
    }
  `,
});

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  margin: ${({ margin }) => margin};
  ${containerTheme};
  ${descriptionTransition};
`;

const checkmarkTheme = styledTheme('product', {
  light: css<Record<string, boolean>>`
    background: ${colors.fullmoon};

    div:hover > & {
      background: ${colors.light};
      box-shadow: 0px 2px 4px ${colors.silver};
    }

    ${({ selected }) =>
      selected &&
      css`
        color: ${colors.light};
        background: ${colors.echo};
        border: 1px solid ${rgba(colors.stardust, 0.6)};
        box-shadow: 0px 2px 4px rgba(58, 170, 63, 0.5);

        div:hover > & {
          background: ${colors.echo};
        }
      `};

    ${({ error }) =>
      error &&
      css`
        border: 1px solid ${colors.mars};
        box-shadow: 0px 2px 6px ${rgba(colors.mars, 0.3)};

        &:hover {
          border: 1px solid ${colors.mars};
          box-shadow: 0px 2px 6px ${rgba(colors.mars, 0.3)};
        }
      `};
  `,
  dark: css<Record<string, boolean>>`
    background: ${colors.blackhole};

    div:hover > & {
      background: ${colors.darkside};
      box-shadow: 0px 2px 4px ${colors.selenium};
    }

    ${({ selected }) =>
      selected &&
      css`
        color: ${colors.light};
        background: ${colors.echo};
        border: 1px solid ${rgba(colors.stardust, 0.6)};
        box-shadow: 0px 2px 4px rgba(58, 170, 63, 0.5);

        div:hover > & {
          background: ${colors.darkside};
        }
      `};

    ${({ error }) =>
      error &&
      css`
        border: 1px solid ${colors.mars};
        box-shadow: 0px 2px 6px ${rgba(colors.mars, 0.5)};

        &:hover {
          border: 1px solid ${colors.mars};
          box-shadow: 0px 2px 6px ${rgba(colors.mars, 0.5)};
        }
      `};
  `,
  alpha: css<Record<string, boolean>>`
    background: ${colors.blackhole};

    div:hover > & {
      background: ${colors.darkside};
      box-shadow: 0px 2px 4px ${colors.selenium};
    }

    ${({ selected }) =>
      selected &&
      css`
        color: ${colors.light};
        background: ${colors.echo};
        border: 1px solid ${rgba(colors.stardust, 0.6)};
        box-shadow: 0px 2px 4px rgba(58, 170, 63, 0.5);

        div:hover > & {
          background: ${colors.darkside};
        }
      `};

    ${({ error }) =>
      error &&
      css`
        border: 1px solid ${colors.mars};
        box-shadow: 0px 2px 6px ${rgba(colors.mars, 0.5)};

        &:hover {
          border: 1px solid ${colors.mars};
          box-shadow: 0px 2px 6px ${rgba(colors.mars, 0.5)};
        }
      `};
  `,
});

const MenuContainer = styled(Container)`
  margin: 0;
  max-width: unset;
`;

const Checkmark = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  margin-right: 12px;
  transition: all 0.2s ease-in-out;
  font-size: 20px;
  color: transparent;
  border: 1px solid ${colors.steel};
  margin-top: 2px;
  left: 16px;

  ${checkmarkTheme};
`;

const dropdownStyles = {
  control: (
    styles: any,
    {
      isFocused,
      isDisabled,
      theme: { colors: themeColor, customSelect: themedSelect },
      selectProps: { error: hasError },
    }: any,
  ) => ({
    ...styles,
    zIndex: 1,
    background: hasError ? themeColor.background.error : themeColor.background.default,
    // eslint-disable-next-line no-nested-ternary
    borderColor: isDisabled
      ? themeColor.controlBorder.disabled
      : // eslint-disable-next-line no-nested-ternary,
      hasError
      ? themeColor.controlBorder.error
      : isFocused
      ? themeColor.controlBorder.focus
      : themeColor.controlBorder.default,
    borderWidth: hasError || isFocused ? 2 : 1,
    transition: 'all 0.2s ease-in-out, background 0s, border 0s',
    '&:hover': {
      // eslint-disable-next-line no-nested-ternary
      borderColor: hasError
        ? themeColor.controlBorder.error
        : isFocused
        ? themeColor.controlBorder.focus
        : themeColor.controlBorder.hover,
      // eslint-disable-next-line no-nested-ternary
      transform: isFocused ? 'translateY(0)' : 'translateY(-2px)',
      boxShadow: `0px 3px 6px ${rgba(colors.silver, 0.6)}`,
    },
    boxShadow: 'none',
    width: '100%',
    'min-height': '48px',
    [`@media only screen and (min-width: ${breakpoints.screenDesktop})`]: {
      'min-height': '40px',
    },
    ...themedSelect,
  }),
  valueContainer: (styles: any, { isMulti, theme: { spacing, customValueContainer: themedValueContainer } }: any) => ({
    ...styles,
    flexWrap: isMulti ? 'wrap' : 'nowrap',
    overflow: 'hidden',
    maxWidth: 'calc(100% - 50px)',
    // eslint-disable-next-line no-nested-ternary
    padding: isMulti ? '6px 8px' : `${spacing.baseUnit / 2}px ${spacing.baseUnit * 2 + spacing.menuGutter - 3}px`,
    [`@media only screen and (min-width: ${breakpoints.screenDesktop})`]: {
      // eslint-disable-next-line no-nested-ternary
      padding: isMulti
        ? '0px 0px 0px 8px'
        : `${spacing.baseUnit / 2}px ${spacing.baseUnit * 2 + spacing.menuGutter - 3}px`,
    },
    height: isMulti ? 'auto' : '38px',
    '& img': {
      marginBottom: '4px',
    },
    marginLeft: '',
    ...themedValueContainer,
  }),
  input: (styles: any, { isDisabled, theme: { colors: themeColor } }: any) => ({
    ...styles,
    color: isDisabled ? themeColor.singleValue.disabled : themeColor.singleValue.default,
    marginTop: '',
  }),
  singleValue: (
    styles: any,
    { data, isDisabled, theme: { spacing, colors: themeColor, customSingleValue: themedSingleValue } }: any,
  ) => ({
    ...styles,
    color: isDisabled ? themeColor.singleValue.disabled : themeColor.singleValue.default,
    paddingLeft: data.imageUrl ? `${spacing.baseUnit * 5 + spacing.menuGutter}px` : '0px',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: `${spacing.baseUnit * 2}px center`,
    backgroundSize: '24px',
    bottom: '-4px',
    top: '',
    marginLeft: '',
    lineHeight: '',
    fontFamily: 'inherit',
    ...themedSingleValue,
  }),
  dropdownIndicator: (styles: any, { theme: { spacing } }: any) => ({
    padding: `0 ${5 * spacing.menuGutter}px 0 9px`,
    position: 'absolute',
    top: 0,
    right: 0,
  }),
  menuList: (styles: any, { theme: { customMenuList: themedMenuList } }: any) => ({
    ...styles,
    padding: 4,
    ...themedMenuList,
  }),
  menu: (styles: any, { theme: { borderRadius, colors: themeColor }, selectProps: { menuColumns } }: any) => ({
    ...styles,
    zIndex: 4,
    border: `1px solid ${themeColor.menu.border}`,
    borderRadius: `${borderRadius}`,
    boxShadow: `0px 3px 6px ${themeColor.menu.shadow}`,
    backgroundColor: themeColor.background.default,
    width: menuColumns ? 'auto' : '100%',
    overflow: 'hidden',
  }),
  container: (styles: any, { theme: { fontFamily } }: any) => ({
    ...styles,
    fontFamily,
    fontSize: 16,
    fontWeight: 'normal',
  }),
  group: (styles: any) => ({
    ...styles,
    paddingBottom: 0,
    paddingTop: 0,
  }),
  groupHeading: (styles: any, { theme: { colors: themeColor, fontFamily } }: any) => ({
    ...styles,
    fontFamily,
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: '12px',
    color: themeColor.group.default,
    textAlign: 'center',
    letterSpacing: '1px',
    marginBottom: '6px',
    marginTop: '10px',
    position: 'relative',
    '& span': {
      background: themeColor.background.default,
    },
  }),
  option: (
    styles: any,
    { data, isMulti, isSelected, isFocused, theme: { colors: themeColor, spacing, customOption: themedOption } }: any,
  ) => ({
    ...styles,
    fontFamily: 'inherit',
    borderRadius: 8,
    padding: `${spacing.baseUnit * 2}px ${spacing.baseUnit * 2}px`,
    color: isSelected ? themeColor.option.selected : themeColor.option.default,
    // eslint-disable-next-line no-nested-ternary
    backgroundColor: isSelected
      ? themeColor.optionBackground.selected
      : isFocused
      ? themeColor.optionBackground.hover
      : themeColor.optionBackground.default,
    '&:hover': {
      color: themeColor.option.selected,
      backgroundColor: isSelected ? themeColor.optionBackground.selected : themeColor.optionBackground.hover,
    },
    backgroundImage: data.imageUrl ? `url('${data.imageUrl}')` : 'initial',
    // eslint-disable-next-line no-nested-ternary
    paddingLeft: data.imageUrl ? (isMulti ? '80px' : `${spacing.baseUnit * 8}px`) : `${spacing.baseUnit * 2}px`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: isMulti ? '44px' : `${spacing.baseUnit * 2}px center`,
    backgroundSize: '24px',
    ...themedOption,
  }),
  multiValue: (styles: any, { selectProps: { error: hasError }, isDisabled, theme: { colors: themeColor } }: any) => ({
    ...styles,
    borderRadius: '4px',
    backgroundColor: hasError && !isDisabled ? themeColor.multiValueLabel.error : themeColor.multiValueLabel.default,
    paddingLeft: '8px',
    ':hover': {
      color: colors.blackhole,
      padding: '0px',
      height: 'fit-content',
      marginTop: '3px',
      paddingLeft: '8px',
    },
  }),
  multiValueLabel: (styles: any, { data, isDisabled, theme: { colors: themeColor } }: any) => ({
    ...styles,
    color: isDisabled ? themeColor.multiValueRemove.disabled : themeColor.multiValueRemove.default,
    backgroundImage: data.imageUrl ? `url('${data.imageUrl}')` : 'initial',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '8px center',
    backgroundSize: '24px',
    paddingLeft: data.imageUrl ? '44px' : '8px',
    backgroundColor: 'none',
  }),
  multiValueRemove: (
    styles: any,
    { selectProps: { error: hasError }, isDisabled, theme: { colors: themeColor } }: any,
  ) => ({
    ...styles,
    color: isDisabled ? themeColor.multiValueRemove.disabled : themeColor.multiValueRemove.default,
    background: 'none',
    padding: '0 6px 0 0',
    border: 'none',
    ':hover': {
      color: colors.blackhole,
      // eslint-disable-next-line no-nested-ternary
      background: themeColor.multiValueRemoveBackground
        ? hasError
          ? themeColor.multiValueRemoveBackground.error
          : themeColor.multiValueRemoveBackground.default
        : hasError
        ? rgba(colors.halo, 0.5)
        : rgba(colors.triton, 0.5),
      padding: '0px',
      height: 'fit-content',
      marginTop: '3px',
      marginRight: '6px',
    },
  }),
  clearIndicator: () => ({
    display: 'none',
  }),
};

export {
  Wrapper,
  Description,
  ImgStyled,
  DropdownIcon,
  RemoveIcon,
  Label,
  dropdownStyles,
  Checkmark,
  MenuContainer,
  PlaceholderIcon,
};
